// Typo tolerance. Shoppers type "ordr" and "recieve"; stemming doesn't help
// with either, and an FAQ bot that answers nothing because of a slipped key
// is the single most annoying failure mode there is.

// Optimal string alignment (Damerau-Levenshtein restricted to adjacent
// transpositions) rather than plain Levenshtein, because transposition is the
// most common typo class of all — "recieve"/"receive" is one edit here, two
// under plain Levenshtein, which would put it out of budget.
//
// Bounded: returns `max + 1` as soon as the answer is known to exceed `max`,
// rather than computing the true distance. Callers only ever ask "is this
// within budget", so the exact value beyond the budget is wasted work.
export function editDistance(a: string, b: string, max: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let beforePrevious: number[] = [];
  let previous: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  let current: number[] = new Array(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    let rowMinimum = current[0];

    for (let j = 1; j <= b.length; j++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;

      let value = Math.min(
        current[j - 1] + 1, // insertion
        previous[j] + 1, // deletion
        previous[j - 1] + substitutionCost,
      );

      const isTransposition = i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1];
      if (isTransposition) {
        value = Math.min(value, beforePrevious[j - 2] + 1);
      }

      current[j] = value;
      if (value < rowMinimum) rowMinimum = value;
    }

    // No later row can score below this row's minimum, so once the whole row
    // is over budget the final distance can only be worse.
    if (rowMinimum > max) return max + 1;

    beforePrevious = previous;
    previous = current;
    current = new Array(b.length + 1);
  }

  return previous[b.length];
}

// Scales with length: on a short word an edit is proportionally huge and
// almost always a different word rather than a typo. This is what keeps "cod"
// (cash on delivery) and "code" (promo code) — both real keywords in the
// seeded FAQ, one edit apart — from collapsing into each other.
export function fuzzyTolerance(word: string): number {
  if (word.length <= 3) return 0;
  if (word.length <= 6) return 1;
  return 2;
}

// Uses the stricter of the two words' budgets, so a long word can never reach
// down and claim a short one.
export function isFuzzyMatch(a: string, b: string): boolean {
  const budget = Math.min(fuzzyTolerance(a), fuzzyTolerance(b));
  if (budget === 0) return false;

  return editDistance(a, b, budget) <= budget;
}
